// src/components/FittingShopQrList.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

const FittingShopQrListDelete = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const q = query(
      collection(db, "fittingshop_qr"),
      orderBy("productCode")
    );

    const fetchOnce = async () => {
      try {
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setRows(data);
      } catch (err) {
        console.error("Error loading fittingshop_qr:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOnce();
  }, []);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Delete this QR/Barcode entry? This cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(id);

      await deleteDoc(doc(db, "fittingshop_qr", id));

      // update UI instantly (no refetch needed)
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Error deleting document:", err);
      alert("Failed to delete entry.");
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      const fields = [
        r.productCode,
        r.description,
        r.customerCode,
        r.customerName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return fields.includes(term);
    });
  }, [rows, search]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-semibold mb-4">
        FittingShop QR / Barcode List
      </h1>

      {/* Controls */}
      <div className="flex flex-col md:flex-row items-start md:items-center mb-6 gap-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by product, description, or customer..."
          className="w-full md:w-1/2 p-2 border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-md p-4">
        {loading ? (
          <p className="text-sm text-gray-600">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-600">
            No QR/Barcode entries found.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-primary border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-white">
                    Product Code
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-white">
                    Description
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-white">
                    Customer Code
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-white">
                    Customer
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-white">
                    QR / Barcode
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-white">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-mono text-gray-700">
                      {row.productCode}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {row.description}
                    </td>
                    <td className="px-4 py-2 text-sm font-mono text-gray-700">
                      {row.customerCode}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {row.customerName}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      {row.sp?.fileWebUrl ? (
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-primary text-white hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary"
                          onClick={() => {
                            window.open(
                              row.sp.fileWebUrl,
                              "_blank",
                              "noopener"
                            );
                          }}
                        >
                          Open QR / Barcode
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">
                          No file
                        </span>
                      )}
                    </td>

                    {/* 🔥 DELETE BUTTON */}
                    <td className="px-4 py-2 text-sm">
                      <button
                        onClick={() => handleDelete(row.id)}
                        disabled={deletingId === row.id}
                        className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {deletingId === row.id
                          ? "Deleting..."
                          : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FittingShopQrListDelete;